import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { WebhookCreateSchema, type WebhookEventType } from '@xsc/shared';
import { api, ApiError } from '../lib/api-client.js';
import { fieldErrors } from '../lib/form-helpers.js';
import { Button } from '../components/ui/button.js';
import { Card, CardBody, CardHeader, CardTitle } from '../components/ui/card.js';
import { Checkbox, Input, Textarea } from '../components/ui/input.js';
import { FormBanner, FormField } from '../components/ui/form.js';

const ALL_EVENTS: { value: WebhookEventType; label: string; description: string }[] = [
  { value: 'event.scheduled',       label: 'event.scheduled',       description: 'Fires when a new event is created or a recurring child is materialized.' },
  { value: 'event.reminder.sent',   label: 'event.reminder.sent',   description: 'Fires for each reminder delivery attempt (per channel).' },
  { value: 'event.confirmed',       label: 'event.confirmed',       description: 'Fires when an event status moves to confirmed.' },
  { value: 'campaign.completed',    label: 'campaign.completed',    description: 'Fires when a campaign finishes all delivery attempts.' },
  { value: 'contact.dnc.added',     label: 'contact.dnc.added',     description: 'Fires when a contact is added to the do-not-call list.' },
];

interface FormState {
  url: string;
  description: string;
  events: WebhookEventType[];
}

const EMPTY: FormState = { url: '', description: '', events: [] };

interface CreateResponse {
  id: string;
  url: string;
  events: string[];
  description?: string;
  active: boolean;
  created_at: string;
  secret: string;
}

export function NewWebhookPage(): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string> | null>(null);
  const [bannerError, setBannerError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreateResponse | null>(null);
  const [copied, setCopied] = useState(false);

  const create = useMutation({
    mutationFn: (payload: unknown) => api.post('/v1/webhooks', payload) as Promise<CreateResponse>,
    onSuccess: (resp) => {
      qc.invalidateQueries({ queryKey: ['webhooks'] });
      setCreated(resp);
    },
    onError: (err) => setBannerError(extractDetail(err)),
  });

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    setBannerError(null);

    const payload: Record<string, unknown> = {
      url: form.url.trim(),
      events: form.events,
    };
    if (form.description.trim()) payload['description'] = form.description.trim();

    const fieldIssues = fieldErrors(WebhookCreateSchema, payload);
    if (fieldIssues) {
      setErrors(fieldIssues);
      return;
    }
    setErrors(null);
    create.mutate(payload);
  };

  const toggleEvent = (e: WebhookEventType): void => {
    setForm((f) => ({
      ...f,
      events: f.events.includes(e) ? f.events.filter((x) => x !== e) : [...f.events, e],
    }));
  };

  const copySecret = async (): Promise<void> => {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard write can fail in non-secure contexts; user can select+copy */
    }
  };

  // ──────────────────────────────────────────────────────────────────────────
  // Post-create reveal: shown exactly once. Server doesn't expose secret on
  // any subsequent read, so the only way to get this back if missed is to
  // delete + recreate the webhook.
  // ──────────────────────────────────────────────────────────────────────────
  if (created) {
    return (
      <div className="max-w-2xl space-y-4">
        <header>
          <h1 className="text-2xl font-semibold">Webhook created</h1>
          <p className="text-sm text-ink-muted">
            Save the signing secret below — it will not be shown again. Use it on your receiver
            to verify the HMAC signature on each delivery.
          </p>
        </header>

        <Card>
          <CardHeader>
            <CardTitle>Signing secret</CardTitle>
          </CardHeader>
          <CardBody className="space-y-3">
            <div className="flex items-stretch gap-2">
              <Input
                value={created.secret}
                readOnly
                onFocus={(e) => e.currentTarget.select()}
                className="font-mono text-xs"
              />
              <Button type="button" variant="secondary" onClick={copySecret}>
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
            <p className="text-xs text-ink-muted">
              The secret is sent in the <code>X-XSC-Signature</code> header as
              {' '}<code>sha256=&lt;hex&gt;</code>. Verify using HMAC-SHA256 over the raw request
              body with this secret as the key.
            </p>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Webhook</CardTitle>
          </CardHeader>
          <CardBody>
            <dl className="grid grid-cols-[120px_1fr] gap-y-2 text-sm">
              <dt className="text-ink-muted">URL</dt>
              <dd className="font-mono text-xs break-all">{created.url}</dd>
              <dt className="text-ink-muted">Events</dt>
              <dd>
                <div className="flex flex-wrap gap-1">
                  {created.events.map((e) => (
                    <span
                      key={e}
                      className="rounded bg-surface-alt border border-border text-ink-muted px-1.5 py-0.5 text-xs"
                    >
                      {e}
                    </span>
                  ))}
                </div>
              </dd>
              {created.description && (
                <>
                  <dt className="text-ink-muted">Description</dt>
                  <dd>{created.description}</dd>
                </>
              )}
            </dl>
          </CardBody>
        </Card>

        <div className="flex justify-end gap-2 pt-2">
          <Button onClick={() => navigate('/webhooks')}>I&apos;ve saved the secret</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-4">
      <header>
        <h1 className="text-2xl font-semibold">New webhook</h1>
        <p className="text-sm text-ink-muted">
          We&apos;ll POST a signed JSON envelope to your URL when any subscribed event fires.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Webhook</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          {bannerError && <FormBanner message={bannerError} />}

          <form onSubmit={onSubmit} className="space-y-4">
            <FormField
              label="URL"
              required
              hint="HTTPS endpoint that receives the POST"
              error={errors?.['url']}
            >
              {(id) => (
                <Input
                  id={id}
                  type="url"
                  value={form.url}
                  onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                  placeholder="https://api.example.com/xsc-webhook"
                  required
                  autoFocus
                />
              )}
            </FormField>

            <fieldset className="rounded border border-border p-4 space-y-3">
              <legend className="px-1 text-sm font-medium text-ink-muted">Events</legend>
              {errors?.['events'] && <p className="text-xs text-danger">{errors['events']}</p>}
              <div className="space-y-2">
                {ALL_EVENTS.map((e) => (
                  <label key={e.value} className="flex items-start gap-2 text-sm">
                    <Checkbox
                      checked={form.events.includes(e.value)}
                      onChange={() => toggleEvent(e.value)}
                      className="mt-0.5"
                    />
                    <span>
                      <code className="text-xs">{e.label}</code>
                      <span className="block text-ink-muted text-xs mt-0.5">{e.description}</span>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>

            <FormField label="Description" hint="Optional, for your own reference" error={errors?.['description']}>
              {(id) => (
                <Textarea
                  id={id}
                  rows={2}
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="e.g., Sync events into Salesforce"
                />
              )}
            </FormField>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={() => navigate('/webhooks')}>
                Cancel
              </Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? 'Creating…' : 'Create webhook'}
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}

function extractDetail(err: unknown): string {
  if (err instanceof ApiError && typeof err.body === 'object' && err.body && 'detail' in err.body) {
    return String((err.body as { detail: unknown }).detail);
  }
  return (err as Error).message;
}
